import { date, duration as durationUtil, string } from '@eti/utils';
import { CarrierLogo, Icon } from '@eti/components';
import { css, etiColors } from '@eti/styles';
import { useDateFormat, useSiteContext, useTranslation } from '@eti/providers';
import { Stack, Text } from '@etg/wings';
import { getBoundTitle } from '../../../trip-details/utils/format';
import useGenerateTripBoundAriaLabel from '../../../../pages/result/components/v3/resultTripCards/components/tripDetailsBound/hooks/useGenerateTripBoundAriaLabel';
import type { TRAIN_SEGMENT_TYPE } from '../../../../pages/result/components/v3/resultTripCards/constants';

const boundInfoStyles = css`
  display: flex;
  position: relative;
`;

const logoHolderStyles = css`
  display: flex;
  position: absolute;
  right: 0;
  top: 0;

  [dir='rtl'] & {
    left: 0;
    right: auto;
  }
`;

const stopsStyles = css`
  display: flex;
  margin: 0 5px;
`;

const stopsIconWrapperStyles = css`
  height: 0.8rem;
  margin: 0 5px;
  width: 0.8rem;
`;

const carrierLogoInfoStyles = css`
  height: 20px;
  margin: 0 4px;
  width: 20px;
`;

const titleStyles = css`
  margin-right: 5px;
`;

interface CartTripBoundInfoProps {
  boundId: string;
  isMultiStop: boolean;
  includesTrainSegments: TRAIN_SEGMENT_TYPE;
  marketingCarriers: { code: string; name: string; logo: { small: string; large: string } }[];
  numberOfTechnicalStops: number;
  segment: any;
  type: string;
}

const CartTripBoundInfo = ({
  boundId,
  isMultiStop,
  includesTrainSegments,
  marketingCarriers,
  numberOfTechnicalStops,
  segment,
  type,
}: CartTripBoundInfoProps) => {
  const { t } = useTranslation();
  const { language, timeFormat } = useSiteContext();
  const { shortWeekdayYear, time } = useDateFormat();
  const title = t(getBoundTitle(type, isMultiStop), parseInt(boundId, 10) + 1);

  const { arrivedAt, duration, departuredAt, destination, origin, stops } = segment;
  const boundAriaLabel = useGenerateTripBoundAriaLabel(
    segment,
    marketingCarriers,
    title,
    includesTrainSegments,
    numberOfTechnicalStops,
  );

  return (
    <Stack aria-label={boundAriaLabel} spacing={0}>
      <Text
        aria-hidden
        as="p"
        className={titleStyles}
        data-testid="cart-trip-bound-info"
        variant="18-emphasized"
      >
        {title}
      </Text>
      <div
        aria-hidden
        className={boundInfoStyles}
        data-testid={`cart-trip-bound-info-date-${boundId}`}
      >
        <Text>{string.firstToUpperCase(date.format(departuredAt, shortWeekdayYear))}</Text>
        {marketingCarriers && (
          <div className={logoHolderStyles}>
            {marketingCarriers.map(({ code, name, logo }) => (
              <CarrierLogo
                className={carrierLogoInfoStyles}
                data-testid={`cart-trip-bound-info-carrierLogo-${code}`}
                id={code || ''}
                imageAlt={name}
                key={`marketing-carrier-${code}`}
                logo={logo}
              />
            ))}
          </div>
        )}
      </div>
      <div
        aria-hidden
        className={boundInfoStyles}
        data-testid={`cart-trip-bound-info-time-${boundId}`}
      >
        <Text as="p" className={titleStyles} variant="16-bold">
          {date.format(departuredAt, time)} - {date.format(arrivedAt, time)}
        </Text>
        {`  (${durationUtil.getDuration(duration, language.code, timeFormat)})`}
        {stops > 0 && (
          <div className={stopsStyles} data-testid={`cart-trip-bound-info-stops-${boundId}`}>
            <div className={stopsIconWrapperStyles}>
              <Icon
                aria-label={t('Air.Filter.Group.Stops')}
                color={etiColors.black}
                id="plane-arrive"
                isGraphic
                size="0.8rem"
              />
            </div>
            <Text>{stops}</Text>
          </div>
        )}
      </div>
      <div
        aria-hidden
        className={boundInfoStyles}
        data-testid={`cart-trip-bound-info-location-${boundId}`}
      >
        <Text>
          {origin.code} {origin.cityName} - {destination.code} {destination.cityName}
        </Text>
      </div>
    </Stack>
  );
};

export default CartTripBoundInfo;
